import React, { useEffect, useState, useMemo } from "react";
import { Box, Typography, TextField, InputAdornment } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";

import Cookies from "universal-cookie";
import Swal from "sweetalert2";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";

import ConsultarReporte from "./ConsultarReporte";
import EliminarReporte from "./EliminarReportes";
import EditarReporte from "./EditaReportes";
import CrearReportes from "./CreaReportes";

function InfoReportes({ setIsAuth }) {
  const { correo } = useParams();
  const navigate = useNavigate();

  const cookie = new Cookies();

  // =========================================================
  // ESTADOS
  // =========================================================

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initDone, setInitDone] = useState(false);
  const [search, setSearch] = useState("");

  // Tipo de usuario:
  // 0 = usuario normal
  // 1 = administrador
  const [tipoUsuario, setTipoUsuario] = useState("");

  // =========================================================
  // FILTRO
  // =========================================================

  const filteredRows = useMemo(() => {
    return rows.filter((row) =>
      Object.values(row).join(" ").toLowerCase().includes(search.toLowerCase()),
    );
  }, [rows, search]);

  // =========================================================
  // CARGAR INFORMACIÓN
  // =========================================================

  useEffect(() => {
    if (!correo) {
      Swal.fire("Error", "No viene correo en la URL", "error");

      navigate("/");
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);

        const isProd = window.location.protocol === "https:";

        // =====================================================
        // CONFIGURACIÓN DE COOKIES
        // =====================================================
        //
        // En producción HTTPS:
        // SameSite=None + Secure
        //
        // En localhost HTTP:
        // SameSite=Lax + Secure=false
        // =====================================================

        const cookieOptions = {
          path: "/",
          secure: isProd,
          sameSite: isProd ? "None" : "Lax",
          maxAge: 7200,
        };

        // =====================================================
        // CONFIGURACIÓN AXIOS
        // =====================================================

        const axiosConfig = {
          auth: {
            username: "webapp",
            password: "762tsfsy9826kk87ytttsf",
          },
        };

        // =====================================================
        // 1. OBTENER ID DEL USUARIO
        // =====================================================

        const idRes = await axios.get(
          "https://reportesicontec.azurewebsites.net/api/ObtenerId?correo=" +
            encodeURIComponent(correo),
          axiosConfig,
        );

        const userId = Array.isArray(idRes.data) ? idRes.data[0] : idRes.data;

        if (!userId) {
          throw new Error("No se encontró el ID del usuario");
        }

        console.log("ID USUARIO:", userId);

        // =====================================================
        // 2. GUARDAR CORREO
        // =====================================================

        cookie.set("correo", correo, cookieOptions);

        // =====================================================
        // 3. GUARDAR ID
        // =====================================================

        cookie.set("ID", userId, cookieOptions);

        // =====================================================
        // 4. OBTENER REPORTES
        // =====================================================

        const reportRes = await axios.get(
          "https://reportesicontec.azurewebsites.net/api/ObtenerReportePorId/" +
            userId,
          axiosConfig,
        );

        const data = Array.isArray(reportRes.data) ? reportRes.data : [];

        console.log("RESPUESTA OBTENER REPORTES:", data);

        // =====================================================
        // 5. DETERMINAR TIPO
        // =====================================================
        //
        // Si existe "idusuario" => TIPO 0
        //
        // Si NO existe "idusuario" => TIPO 1
        // =====================================================

        const tieneIdUsuario = data.some((item) =>
          Object.prototype.hasOwnProperty.call(item, "idusuario"),
        );

        const tipoCalculado = tieneIdUsuario ? 0 : 1;

        console.log("¿TIENE IDUSUARIO?:", tieneIdUsuario);

        console.log("TIPO CALCULADO:", tipoCalculado);

        // =====================================================
        // 6. GUARDAR TIPO EN COOKIE
        // =====================================================

        cookie.set("tipo", String(tipoCalculado), cookieOptions);

        // =====================================================
        // 7. LEER COOKIE
        // =====================================================

        const tipoDesdeCookie = cookie.get("tipo");

        console.log("TIPO LEÍDO DESDE COOKIE:", tipoDesdeCookie);

        console.log("TODAS LAS COOKIES:", cookie.getAll());

        // =====================================================
        // 8. GUARDAR TIPO EN ESTADO
        // =====================================================
        //
        // Usamos el valor calculado para que React
        // inmediatamente sepa si es 0 o 1.
        // =====================================================

        setTipoUsuario(String(tipoCalculado));

        // =====================================================
        // 9. GUARDAR NOMBRE DEL USUARIO
        // =====================================================

        const nombreUsuario = data.find(
          (item) => item.nombreUsuario && item.nombreUsuario.trim() !== "",
        )?.nombreUsuario;

        if (nombreUsuario) {
          cookie.set("nombreUsuario", nombreUsuario, cookieOptions);
        } else {
          cookie.remove("nombreUsuario", {
            path: "/",
          });
        }

        // =====================================================
        // 10. GUARDAR REPORTES
        // =====================================================

        setRows(data);

        // =====================================================
        // 11. FINALIZAR
        // =====================================================

        setInitDone(true);

        if (setIsAuth) {
          setIsAuth(true);
        }

        // =====================================================
        // DEBUG
        // =====================================================

        console.log("====================================");

        console.log("CORREO:", correo);

        console.log("ID:", userId);

        console.log("TIPO CALCULADO:", tipoCalculado);

        console.log("TIPO COOKIE:", cookie.get("tipo"));

        console.log("====================================");
      } catch (error) {
        console.error("ERROR:", error);

        Swal.fire("Error", "No se pudo cargar la información", "error");

        navigate("/");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [correo, navigate, setIsAuth]);

  // =========================================================
  // CARGANDO
  // =========================================================

  if (!initDone) {
    return (
      <Box sx={{ p: 5 }}>
        <Typography>Cargando sesión...</Typography>
      </Box>
    );
  }

  // =========================================================
  // COLUMNAS
  // =========================================================

  const columns = [
    {
      field: "id",
      headerName: "ID",
      width: 80,
    },

    {
      field: "reporte",
      headerName: "Reporte",
      flex: 1,
    },

    {
      field: "consultar",
      headerName: "Consultar",
      width: 130,

      renderCell: (params) => <ConsultarReporte url={params.row.urlReporte} />,
    },

    // =======================================================
    // EDITAR Y ELIMINAR
    // SOLO TIPO 1
    // =======================================================

    ...(tipoUsuario === "1"
      ? [
          {
            field: "editar",
            headerName: "Editar",
            width: 120,

            renderCell: (params) => <EditarReporte idReporte={params.row.id} />,
          },

          {
            field: "eliminar",
            headerName: "Eliminar",
            width: 120,

            renderCell: (params) => <EliminarReporte id={params.row.id} />,
          },
        ]
      : []),
  ];

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <Box sx={{ p: 3 }}>
      {/* =====================================================
          TITULO
      ====================================================== */}

      <Typography variant="h6" mb={2}>
        Reportes
      </Typography>

      {/* =====================================================
          CREAR REPORTE
          SOLO TIPO 1
      ====================================================== */}

      {tipoUsuario === "1" && <CrearReportes />}

      {/* =====================================================
          BUSCADOR
      ====================================================== */}

      <TextField
        fullWidth
        size="small"
        placeholder="Buscar..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {/* =====================================================
          TABLA
      ====================================================== */}

      <Box
        sx={{
          height: "70vh",
          mt: 2,
        }}
      >
        <DataGrid
          rows={filteredRows}
          columns={columns}
          loading={loading}
          pageSize={5}
          disableRowSelectionOnClick
        />
      </Box>
    </Box>
  );
}

export default InfoReportes;
